import React, { useCallback, useEffect, useState } from 'react';
import type { CompanyAgentSettings, AutonomyLevel, ToolPolicy } from '../../types/agent';
import { agentApi, apiErrorMessage } from '../../services/agentApi';
import { btn, card, input, Spinner } from './agentUi';
import MailboxCard from './MailboxCard';
import EmailAliasManager from './EmailAliasManager';
import { useAuth } from '../../contexts/AuthContext';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';

type SettingsResponse = Awaited<ReturnType<typeof agentApi.settings>>;

const AUTONOMY: Array<{ key: AutonomyLevel; title: string; text: string }> = [
  { key: 'shadow', title: 'Shadow', text: 'Arie reads every email and plans the work, but nothing is executed. Each proposed action waits in Approvals — approve to run it. Start here.' },
  { key: 'assisted', title: 'Assisted', text: 'Routine, reversible work runs automatically (filing into a confidently-matched customer, running reports, filling forms, drafting replies). New customers and customer edits wait for your approval.' },
  { key: 'full', title: 'Full', text: 'Everything runs automatically. Arie still never sends email to anyone but you.' },
];

const TOOL_LABELS: Record<string, string> = {
  commit_intake: 'File attachments into a customer folder',
  create_folder: 'Create folders',
  rename_folder: 'Rename folders',
  rename_document: 'Rename documents',
  move_documents: 'Move documents',
  open_workflow: 'Run analyses / reports',
  fill_acord_form: 'Fill ACORD forms',
  fill_template: 'Fill emailed templates',
  generate_certificate: 'Generate certificates',
  import_ams360_customer: 'Import customers from AMS360',
  import_ams360_documents: 'Import documents from AMS360',
  attach_report_to_ams360: 'Attach reports to AMS360',
  create_customer: 'Create customers',
  edit_customer: 'Edit customers',
  save_memory: 'Remember preferences',
  draft_reply_email: 'Draft reply emails',
  draft_writeup: 'Write summaries',
  create_report_pdf: 'Make report PDFs',
};

const POLICY_TEXT: Record<string, string> = { read: 'Always', auto: 'Runs automatically', approval: 'Needs approval', shadow: 'Recorded only (shadow)', never: 'Not allowed' };

const SimulateCard: React.FC<{ onQueued: (runId: string) => void }> = ({ onQueued }) => {
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const submit = async () => {
    if (!file) return;
    setBusy(true);
    setResult(null);
    try {
      const r = await agentApi.simulate(file);
      setResult(r.outcome === 'queued' ? 'Queued — opening the run…' : `Outcome: ${r.outcome}${r.reason ? ` (${r.reason})` : ''}`);
      if (r.runId) onQueued(r.runId);
    } catch (err) {
      setResult(apiErrorMessage(err, 'Simulation failed'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className={`${card} p-4`}>
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Simulate an email</h3>
      <p className="text-xs text-gray-500 mt-1">Upload a saved .eml or .msg — it goes through the exact inbound pipeline, as you. (Testing / local stack.)</p>
      <div className="flex items-center gap-2 mt-3">
        <input type="file" accept=".eml,.msg" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm" />
        <button className={btn.primary} onClick={submit} disabled={!file || busy}>{busy ? 'Sending…' : 'Run it'}</button>
      </div>
      {result && <p className="text-xs mt-2 text-gray-700 dark:text-gray-200">{result}</p>}
    </div>
  );
};

const AgentSettingsPanel: React.FC<{ onOpenRun: (runId: string) => void }> = ({ onOpenRun }) => {
  const features = useFeatureFlags();
  const { user } = useAuth();
  const [data, setData] = useState<SettingsResponse | null>(null);
  const [draft, setDraft] = useState<CompanyAgentSettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await agentApi.settings();
      setData(d);
      setDraft(d.settings);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not load settings'));
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  // The server enforces the real rule (sysadmin, or company admin outside production).
  const canSimulate = user?.role === 'system_admin' || user?.role === 'admin';

  if (!data) return <div className="flex items-center gap-2 text-gray-500 py-10 justify-center">{error ? <span className="text-red-600 dark:text-red-400">{error}</span> : <><Spinner /> Loading…</>}</div>;

  const editable = data.canEdit && !!draft;
  const set = <K extends keyof CompanyAgentSettings>(k: K, v: CompanyAgentSettings[K]) => {
    setDraft((d) => (d ? { ...d, [k]: v } : d));
    setSaved(false);
  };
  const setOverride = (tool: string, value: ToolPolicy | '') => {
    if (!draft) return;
    const next = { ...(draft.toolOverrides || {}) };
    if (value) next[tool] = value;
    else delete next[tool];
    set('toolOverrides', next);
  };

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    setError(null);
    try {
      await agentApi.saveSettings(draft);
      await load();
      setSaved(true);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not save'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className="space-y-4">
        <MailboxCard />
        <EmailAliasManager />
        {canSimulate && <SimulateCard onQueued={onOpenRun} />}
      </div>

      <div className={`${card} p-4 space-y-5`}>
        <div className="flex items-start justify-between gap-2">
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Agency agent settings</h3>
            <p className="text-xs text-gray-500 mt-0.5">{data.runsToday} run{data.runsToday === 1 ? '' : 's'} today{draft ? ` of ${draft.dailyRunCap} allowed` : ''}.{!data.canEdit && ' Only company admins can change these.'}</p>
          </div>
          {!data.platformEnabled && <span className="text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/30 px-2 py-1 rounded">Paused platform-wide</span>}
        </div>

        {!draft ? (
          <p className="text-sm text-gray-500">The agent needs a company account.</p>
        ) : (
          <>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={draft.enabled} disabled={!editable} onChange={(e) => set('enabled', e.target.checked)} />
              <span className="text-gray-900 dark:text-gray-100 font-medium">Handle email sent to the agent mailbox</span>
            </label>

            <div>
              <div className="text-xs uppercase tracking-wide text-gray-500 mb-2">Autonomy</div>
              <div className="space-y-2">
                {AUTONOMY.map((a) => (
                  <label key={a.key} className={`block border rounded-md p-2.5 cursor-pointer ${draft.autonomyLevel === a.key ? 'border-primary bg-primary/5' : 'border-gray-200 dark:border-gray-700'}`}>
                    <div className="flex items-center gap-2">
                      <input type="radio" name="autonomy" checked={draft.autonomyLevel === a.key} disabled={!editable} onChange={() => set('autonomyLevel', a.key)} />
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{a.title}</span>
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-300 mt-1 ml-6">{a.text}</p>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <div className="text-xs uppercase tracking-wide text-gray-500 mb-2">What Arie may do by email</div>
              <table className="w-full text-xs">
                <thead className="text-gray-500">
                  <tr>
                    <th className="text-left font-medium py-1">Action</th>
                    <th className="text-left font-medium py-1">Effective</th>
                    <th className="text-left font-medium py-1">Override</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {data.policy.map((p) => (
                    <tr key={p.tool}>
                      <td className="py-1.5 pr-2 text-gray-800 dark:text-gray-100">{p.tool === 'generate_certificate' && !features.acord ? 'Generate Certificates of Effort' : TOOL_LABELS[p.tool] || p.tool}</td>
                      <td className="py-1.5 pr-2 text-gray-600 dark:text-gray-300">{POLICY_TEXT[p.policy] || p.policy}</td>
                      <td className="py-1.5">
                        {['draft_reply_email', 'draft_writeup', 'create_report_pdf'].includes(p.tool) ? (
                          <span className="text-gray-400">always (drafts only)</span>
                        ) : (
                          <select className="text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-1 py-0.5" disabled={!editable} value={draft.toolOverrides?.[p.tool] || ''} onChange={(e) => setOverride(p.tool, e.target.value as ToolPolicy | '')}>
                            <option value="">Default</option>
                            <option value="auto">Automatic</option>
                            <option value="approval">Approval</option>
                            <option value="never">Never</option>
                          </select>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-[11px] text-gray-500 mt-1">Deleting folders or documents is never available by email. Arie never emails clients — drafted replies are for you to send from your own email.</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <label className="block text-xs">
                <span className="text-gray-500">Runs per day (company)</span>
                <input className={input} type="number" min={0} max={1000} value={draft.dailyRunCap} disabled={!editable} onChange={(e) => set('dailyRunCap', Number(e.target.value))} />
              </label>
              <label className="block text-xs">
                <span className="text-gray-500">Emails per sender per hour</span>
                <input className={input} type="number" min={0} max={1000} value={draft.perSenderHourlyCap} disabled={!editable} onChange={(e) => set('perSenderHourlyCap', Number(e.target.value))} />
              </label>
            </div>

            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" className="mt-1" checked={draft.summaryEmailEnabled} disabled={!editable} onChange={(e) => set('summaryEmailEnabled', e.target.checked)} />
              <span>
                <span className="text-gray-900 dark:text-gray-100">Email a summary back after every run</span>
                <span className="block text-xs text-gray-500">Includes links to everything produced and anything waiting for approval.</span>
              </span>
            </label>

            {editable && (
              <div className="flex items-center gap-3">
                <button className={btn.primary} onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save settings'}</button>
                {saved && <span className="text-xs text-green-700 dark:text-green-400">Saved.</span>}
                {error && <span className="text-xs text-red-600 dark:text-red-400">{error}</span>}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AgentSettingsPanel;
